import { useEffect, useRef } from 'react';
import { apm } from '@elastic/apm-rum';
import { parseUserAgent } from 'react-device-detect';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { User } from '../../models/User';
import { getSessionCompanyId } from '../../selectors/session';
import { getUser } from '../../selectors/user';
import {
  marketingTrackingEmitter,
  MarketingTrackingEmitterEventType,
} from '../emitters/marketingTrackingEmitter';
import useUtmParams from '../hooks/useUtmParams';
import { UserAgentData } from '../interfaces/user-agent';

export type MarketingTrackingProps = {
  event: string;
  payload: Record<string, unknown>;
};

type UseMarketingTrackingServiceProps = {
  listenToMarketingTrackingEvents: boolean;
};

const DEFAULT_DELAY = 300;
export default function useMarketingTrackingService(
  { listenToMarketingTrackingEvents }: UseMarketingTrackingServiceProps = {
    listenToMarketingTrackingEvents: false,
  }
) {
  const user: User = useSelector(getUser);
  const location = useLocation();

  const companyId = useSelector(getSessionCompanyId);

  const userAgentData: UserAgentData = parseUserAgent(
    window.navigator.userAgent
  );
  const { utmParams } = useUtmParams();
  const searchParams = new URLSearchParams(location.search);

  /* eslint-disable camelcase */
  const campaignParams = {
    campaign_id: searchParams.get('campaign_id'),
    channel: searchParams.get('channel'),
    ad_group_id: searchParams.get('ad_group_id'),
    ad_creative_id: searchParams.get('ad_creative_id'),
  };
  /* eslint-enable camelcase */

  /* eslint-disable camelcase */
  const additionalProperties = {
    ...(user && { user_id: user.id }),
    ...(companyId && { company_id: companyId }),
    device_model: userAgentData.device?.model,
    device_vendor: userAgentData.device?.vendor,
    os_name: userAgentData.os?.name,
    os_version: userAgentData.os?.version,
    page_url: window.location.href,
    page_path: window.location.pathname,
    ...utmParams,
    ...campaignParams,
  };
  /* eslint-enable camelcase */

  const sendGA4Event = ({ event, payload }: MarketingTrackingProps) => {
    if (window?.gtag === undefined) {
      // add small delay to ensure the gtag script is loaded
      setTimeout(() => {
        sendGA4Event({ event, payload });
      }, DEFAULT_DELAY);
      return;
    }

    if (typeof window.gtag !== 'function') {
      return;
    }
    window.gtag('event', event, {
      // eslint-disable-next-line camelcase
      app_name: 'emp_web',
      ...additionalProperties,
      ...payload,
    });
  };

  const sendAirbridgeEvent = ({ event, payload }: MarketingTrackingProps) => {
    if (window?.airbridge?.events?.send === undefined) {
      // add small delay to ensure the airbridge script is loaded
      setTimeout(() => {
        sendAirbridgeEvent({ event, payload });
      }, DEFAULT_DELAY);
      return;
    }

    if (typeof window.airbridge?.events?.send !== 'function') {
      return;
    }
    window.airbridge.events.send(event, {
      label: event,
      customAttributes: {
        ...additionalProperties,
        ...payload,
      },
    });
  };

  const sendFacebookEvent = ({ event, payload }: MarketingTrackingProps) => {
    if (!window.fbq) {
      setTimeout(() => {
        sendFacebookEvent({ event, payload });
      }, DEFAULT_DELAY);
      return;
    }
    const MAX_EVENT_NAME_LENGTH = 50;
    if (event.length > MAX_EVENT_NAME_LENGTH) {
      apm.captureError(new Error(`Event name is too long${event}`));
    }

    window.fbq('trackCustom', event.slice(0, MAX_EVENT_NAME_LENGTH), {
      ...additionalProperties,
      ...payload,
    });
  };

  const sendMarketingTrackingEvent = (props: MarketingTrackingProps) => {
    sendGA4Event(props);
    sendAirbridgeEvent(props);
    sendFacebookEvent(props);
  };

  const sendMarketingTrackingEventRef = useRef(sendMarketingTrackingEvent);
  sendMarketingTrackingEventRef.current = sendMarketingTrackingEvent;

  useEffect(() => {
    if (!listenToMarketingTrackingEvents) return;
    const handleMarketingTrackingEvent = (data: MarketingTrackingProps) => {
      sendMarketingTrackingEventRef.current(data);
    };
    marketingTrackingEmitter.on(
      MarketingTrackingEmitterEventType,
      handleMarketingTrackingEvent
    );

    return () => {
      marketingTrackingEmitter.off(
        MarketingTrackingEmitterEventType,
        handleMarketingTrackingEvent
      );
    };
  }, [listenToMarketingTrackingEvents]);

  return {
    sendMarketingTrackingEvent,
  } as const;
}
